<script setup>
import {ref} from "vue";
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import {useLanguageStore} from "@/pinia";
import {useToast} from "@/composables/useToast";

const toast = useToast();
const languageStore = useLanguageStore();

// Form ref
const formRef = ref(null);
const isFormValid = ref(false);

// Validation rules
const rules = {
  required: v => !!v || languageStore.get('requiredField')
};

const ageTitle = ref("");
const startDt = ref("");
const endDt = ref("");
const ageOrder = ref("");

const props = defineProps({
  isAgeInsertModal: Boolean,
})

const emits = defineEmits([
  'set_IsAgeInsertModal',
  'fn_saveAge'
])

const close = () => {
  ageTitle.value = "";
  startDt.value = "";
  endDt.value = "";
  ageOrder.value = null;
  formRef.value?.reset();
  emits('set_IsAgeInsertModal', false);
}

const save = async () => {
  // v-form validation
  const {valid} = await formRef.value.validate();

  // Custom validation for VueDatePicker
  if (!startDt.value) {
    toast.warning(languageStore.get('pleaseSelectStartDate'));
    return;
  }
  if (!endDt.value) {
    toast.warning(languageStore.get('pleaseSelectEndDate'));
    return;
  }

  if (!valid) {
    toast.warning(languageStore.get('pleaseCheckInputFields'));
    return;
  }

  let newAge = {
    "ageTitle": ageTitle.value,
    "startDt": startDt.value,
    "endDt": endDt.value,
    "ageOrder": ageOrder.value
  }
  emits('fn_saveAge', newAge);
  close();
}
</script>

<template>
  <v-dialog
      :model-value="isAgeInsertModal"
      @update:model-value="close"
      width="500"
  >
    <v-card>
      <v-card-title class="modal-header">
        {{ languageStore.get('sessionRegistration') }}
      </v-card-title>

      <v-card-text class="modal-body">
        <v-form ref="formRef" v-model="isFormValid">
          <v-row>
            <v-col cols="6">
              <vue-date-picker
                  v-model="startDt"
                  locale="ko"
                  :enable-time-picker="false"
                  :placeholder="languageStore.get('startDate') + ' *'"
                  class="dp-full-width"
                  :teleport="true"
              />
            </v-col>
            <v-col cols="6">
              <vue-date-picker
                  v-model="endDt"
                  locale="ko"
                  :enable-time-picker="false"
                  :placeholder="languageStore.get('endDate') + ' *'"
                  class="dp-full-width"
                  :teleport="true"
              />
            </v-col>
          </v-row>

          <v-text-field
              v-model="ageTitle"
              :label="languageStore.get('sessionName') + ' *'"
              variant="outlined"
              density="compact"
              hide-details="auto"
              class="mb-4 mt-4"
              :rules="[rules.required]"
          />

          <v-text-field
              v-model="ageOrder"
              :label="languageStore.get('sortOrder')"
              type="number"
              variant="outlined"
              density="compact"
              hide-details="auto"
          />
        </v-form>
      </v-card-text>

      <v-card-actions class="modal-footer">
        <v-spacer />
        <v-btn
            color="primary"
            variant="flat"
            @click="save"
        >
          {{ languageStore.get('save') }}
        </v-btn>
        <v-btn
            variant="outlined"
            @click="close"
        >
          {{ languageStore.get('close') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
/* 모달 헤더 */
.modal-header {
  background-color: #1A3A5C !important;
  color: #FFFFFF !important;
  padding: 16px 24px;
  font-weight: 600;
  font-size: 18px;
}

/* 모달 바디 - Vuetify 기본 padding 제거 */
:deep(.v-card-text.modal-body) {
  padding: 24px !important;
}

/* 모달 바디 내 입력 필드 간격 */
.modal-body :deep(.v-input),
.modal-body :deep(.dp-full-width) {
  margin-bottom: 16px;
}

/* v-row 내부의 v-input은 margin 제외 */
.modal-body :deep(.v-row .v-input),
.modal-body :deep(.v-row .dp-full-width) {
  margin-bottom: 0;
}

.modal-body :deep(.v-row) {
  margin-top: 0;
  margin-bottom: 16px;
}

.modal-body :deep(.v-col) {
  padding-top: 0;
  padding-bottom: 0;
}

.modal-body :deep(.v-input:last-child) {
  margin-bottom: 0;
}

/* 모달 푸터 - Vuetify 기본 padding 제거 */
:deep(.v-card-actions.modal-footer) {
  padding: 16px 24px !important;
  border-top: 1px solid #DEE2E6;
  gap: 8px;
}

/* DatePicker 전체 너비 */
.dp-full-width {
  width: 100%;
}

.dp-full-width :deep(.dp__input) {
  height: 40px;
  border-radius: 4px;
  border-color: #C7C7C7;
}

.dp-full-width :deep(.dp__input:hover) {
  border-color: #1A3A5C;
}
</style>
